import { BY_GENOME, BY_MUTATION, BY_MUTATION_BLOCK, getPrevalenceColor } from './common';
import { LIGHT_GENE_BG, LIGHT_GENE_BG_MID, DARK_GENE_BG,
  SEQUENCE_TICK_STROKE_WEIGHT, MUTATION_TICK_STROKE_WEIGHT,
  MUTATION_BLOCK_STROKE_WEIGHT
} from './styles';
import { MutationBlockHorizontal } from './mutationblockhorizontal';
import { MutationBlockRadial } from './mutationblockradial';


export default class Variant {

  constructor(name, count) {
    this.name = name;
    this.label = name.indexOf('.') >= 0 ? name : name[0].toUpperCase() === 'X' ? name.toUpperCase() : (name[0].toUpperCase() + name.substring(1).toLowerCase());
    this.count = count;
    this.mutations = {};
    /* these Proxies act like defaultdicts in python: if the key
    doesn't already exist, we create it.
    */
    this.genes = new Proxy({}, {
      get: (target, name) => name in target ? target[name]
        : (target[name] = new Proxy([], {
          get: (target2, index) => target2[index] !== undefined ? target2[index] : (target2[index] = [], target2[index])
        }), target[name])
    });
  }

  setMutation(mutation, count) {
    let pct = count / this.count;
    // let currentMutation = this.genes[mutation.gene][mutation.position];
    this.genes[mutation.gene][mutation.position].push({mutation, count, pct});
    this.mutations[mutation.name] = {count, pct};
  }

  sortPositionedMutations() {
    Object.values(this.genes).forEach(positionList=>{
      positionList.forEach(mutationList=>mutationList.sort((a,b)=>b.count - a.count));
    });
  }

  getPercent(name) {
    return this.mutations[name] === undefined ? 0 : this.mutations[name].pct;
  }

  getCount(name) {
    return this.mutations[name] === undefined ? 0 : this.mutations[name].count;
  }

  setRadii(inner, outer, labelY) {
    this.inner = inner;
    this.outer = outer;
    this.labelY = labelY;
  }

  setYPosition(top, height) {
    this.y1 = top;
    this.height = height;
    this.y2 = top + height;
  }

  getVisibleMutations(threshold) {
    let visibles = [];
    for (let gene in this.genes) {
      /*
      this grabs the most prominent mutation at each location for this lineage,
      and then checks whether it is above the requested threshhold
      */
      this.genes[gene].map(m=>m[0]).filter(m=>m && m.pct >= threshold).forEach(({mutation, count, pct})=>visibles.push({mutation, count, pct}));
    }
    return visibles;
  }


  setMutationPositions(mutLookup, genes, labelList, threshold,
    bySequence, BlockConstructor, blockPositionCallback, allowWildTypeOverride) {
    const dynaWide = bySequence !== BY_GENOME;
    const exactMatch = bySequence === BY_MUTATION;

    const DEFAULT = {name: '', count: 0, pct: 0};
    let blocks = [];
    labelList.forEach((label, index)=>{
      let {items} = label,
        first = items[0].mutation,
        gene = first.gene,
        name, stats,
        mutation = first,
        isWildType = false;
      // if (this.name === 'BA.2.75.2' && gene === 'S' && first.position === 440) {
      //   console.log(this.name, this.count, bySequence, exactMatch, this.mutations['S:N440K'], this.genes[gene][first.position]);
      // }
      if (genes[gene]) {
        if (items.length === 0 || exactMatch) {
          name = first.name;
          mutation = first;
          stats = this.mutations[name] || DEFAULT;
        } else {
          try {
            let {gene, position} = first,
              atSite = this.genes[gene][position];
            if (atSite) {
              stats = atSite[0];
              /*
              the first element in the array is the most common mutation
              but could the wild type be more common?
              if this mutation is more than 50% of the total, then no.
              otherwise, total up the mutations to find the wild type count.
              */
              if (allowWildTypeOverride && stats.count < this.count * 0.5) {
                let mTot = atSite.reduce((tot, s)=>tot + (s.count || 0), 0),
                  wildCount = this.count - mTot;
                if (wildCount > stats.count) {
                  stats = {
                    /*
                    we want the label to show up as gray,
                    since the color corresponds to the frequency
                    of mutations.
                    so fudge this data to go gray
                    */
                    count: 0, // wildCount,
                    pct : 0 // wildCount / this.count
                  };
                  isWildType = true;
                }
              } else {
                mutation = stats.mutation;
              }
              name = mutation.name;
            } else {
              console.log(this.name, first)
            }
          } catch (err) {
            //
          }
        }
        // console.log(this.name, first);
        let {count, pct} = (stats || DEFAULT),
          info = mutLookup[name] || mutLookup[first.name],
          block = new BlockConstructor(mutation, pct, count, isWildType),
          nextLabel = labelList[index+1];
        blockPositionCallback(block, info, label, nextLabel, count, pct, dynaWide);
        block.color = pct >= threshold ? info.color : LIGHT_GENE_BG;
        blocks.push(block);
        label.add(block);
      }
    });
    this.blocks = blocks;
  }



  setMutationPositionsHorizontal(mutLookup, genes, labelList, threshold,
    top, height, lineWidth, bySequence, allowWildTypeOverride) {
    this.y1 = top;
    this.height = height;
    this.y2 = top + height;
    const halfWidth = lineWidth * 0.5;
    let blockPositioner = (block, info, label, nextLabel, count, pct, dynaWide)=>{
      let x = label.x,
        w = lineWidth;
      if (dynaWide && nextLabel && nextLabel.items[0].mutation.gene === label.items[0].mutation.gene) {
        let x2 = nextLabel.x || x + lineWidth;
        w = x2 - x - 1;
      }
      block.x = x - halfWidth;
      block.y = top;
      block.w = w;
      block.h = height;
    }
    this.setMutationPositions(mutLookup, genes, labelList, threshold,
      bySequence, MutationBlockHorizontal, blockPositioner, allowWildTypeOverride);
  }





  setMutationPositionsRadial(mutLookup, genes, labelList, threshold, bySequence, allowWildTypeOverride) {
    let {inner, outer} = this;
    /* */
    let blockPositioner = (block, info, label, nextLabel, count, pct, dynaWide)=>{ // eslint-disable-line
      let {angle, cosA, sinA } = label;
      block.angle = angle;
      block.cosA = cosA;
      block.sinA = sinA;
      block.r1 = inner;
      block.r2 = outer;
    }
    this.setMutationPositions(mutLookup, genes, labelList, threshold,
      bySequence, MutationBlockRadial, blockPositioner, allowWildTypeOverride);
  }


  colorByPrevalence() {
    // let low = darkMode ? PREVALENCE_COLOR_LOW_DARKMODE : PREVALENCE_COLOR_LOW_LIGHTMODE,
    //   high = darkMode ? PREVALENCE_COLOR_HIGH_DARKMODE : PREVALENCE_COLOR_HIGH_LIGHTMODE,
    //   zero = darkMode ? PREVALENCE_COLOR_ZERO_DARKMODE : PREVALENCE_COLOR_ZERO_LIGHTMODE;
    this.blocks.forEach(block=>{
      // if (this.name === 'BA.2.12.1' && block.mutation.name === 'ORF7a:I110T') {
      //   console.log('debug', this.name, block.mutation.name, block.pct);
      // }
      block.color = getPrevalenceColor(block.pct);
    });
  }


  drawGeneWedge(ctx, bySequence, darkMode, geneWedge) {
    let {sa, ea, cs, ss, ce, se, name, visibles} = geneWedge;
    if (ea > sa || visibles.length > 0) {
      this.drawWedge(ctx, bySequence, darkMode, sa, ea, cs, ss, ce, se, name);
    }
  }


  drawGeneBar(ctx, bySequence, darkMode, highlightGene, geneWedge) {
    let {x1, width, name} = geneWedge;
    // if (width > 0 || visibles.length > 0) {
    this.drawBar(ctx, bySequence, darkMode, highlightGene, x1, width, name);
    // }
  }


  drawWedge(ctx, bySequence, darkMode, sa, ea, cs, ss, ce, se) {
    let {inner, outer} = this,
      outerFull = outer,
      strokeWidth;
    switch(bySequence) {
    case BY_GENOME: strokeWidth = SEQUENCE_TICK_STROKE_WEIGHT; break;
    case BY_MUTATION: strokeWidth = MUTATION_TICK_STROKE_WEIGHT; break;
    case BY_MUTATION_BLOCK: strokeWidth = MUTATION_BLOCK_STROKE_WEIGHT; break;
    }
    strokeWidth *= 0.5;
    this.setGeneBG(ctx, darkMode, null, name);
    outer -= strokeWidth;
    inner += strokeWidth;
    ctx.strokeStyle = ctx.fillStyle;
    ctx.beginPath();
    ctx.moveTo(cs * outer, ss * outer);
    ctx.arc(0, 0, outer, sa, ea);
    ctx.lineTo(ce * inner, se * inner);
    ctx.arc(0, 0, inner, ea, sa, true);
    ctx.lineTo(cs * outerFull, ss * outerFull);
    ctx.fill();
    ctx.stroke();
  }

  drawBar(ctx, bySequence, darkMode, highlightGene, x1, width) {
    let {y1, height} = this,
      strokeWidth;
    switch(bySequence) {
    case BY_GENOME: strokeWidth = SEQUENCE_TICK_STROKE_WEIGHT; break;
    case BY_MUTATION: strokeWidth = MUTATION_TICK_STROKE_WEIGHT; break;
    case BY_MUTATION_BLOCK: strokeWidth = MUTATION_BLOCK_STROKE_WEIGHT; break;
    }
    this.setGeneBG(ctx, darkMode, highlightGene, name);
    ctx.fillRect(x1, y1, width - strokeWidth, height);
  }

  setGeneBG(ctx, darkMode) {
    if (darkMode) {
      // ctx.fillStyle = highlightGene === null ? DARK_GENE_BG
      //   : geneName === highlightGene ? DARK_GENE_BG
      //     : DARK_GENE_BG_MID;
      ctx.fillStyle = DARK_GENE_BG;
    } else {
      // ctx.fillStyle = highlightGene === null ? LIGHT_GENE_BG
      //   : geneName === highlightGene ? LIGHT_GENE_BG
      //     : LIGHT_GENE_BG_MID;
      ctx.fillStyle = LIGHT_GENE_BG_MID;
    }
  }

  drawMutationsRadial(ctx) {
    ctx.beginPath();
    this.blocks.forEach(b=>b.draw(ctx));
    ctx.stroke();
  }


  drawMutationsHorizontal(ctx) {
    ctx.globalAlpha = 1;
    this.blocks.forEach(b=>b.draw(ctx));
    ctx.globalAlpha = 1;
  }


  drawSubstitutionLetters(ctx) {
    this.blocks.forEach(block=>{
      block.drawLetter(ctx);
    });
  }

  drawHighlightLetter(ctx, highlightIndex) {
    let block = this.blocks[highlightIndex];
    block.drawLetter(ctx);
  }

}


